import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  CmpApiService, CmpConfigDto, CmpConfigProviderDto,
  CmpVendorDto, CreateCmpConfigBody, UpdateCmpConfigBody
} from '../../../services/cmp-api.service';
import { ProvidersApiService, ProviderDto } from '../../../services/providers-api.service';
import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';
import { JsEditorComponent } from '../../shared/js-editor/js-editor.component';

export const IAB_PURPOSES_IT = [
  { id: 1,  name: 'Archiviazione e/o accesso a informazioni su un dispositivo',          legalBasis: 'consent',            required: true },
  { id: 2,  name: 'Utilizzo di dati limitati per selezionare la pubblicità',              legalBasis: 'consent' },
  { id: 3,  name: 'Creazione di profili per pubblicità personalizzata',                   legalBasis: 'consent' },
  { id: 4,  name: 'Utilizzo di profili per selezionare pubblicità personalizzata',        legalBasis: 'consent' },
  { id: 5,  name: 'Creazione di profili per personalizzare i contenuti',                  legalBasis: 'consent' },
  { id: 6,  name: 'Utilizzo di profili per selezionare contenuti personalizzati',         legalBasis: 'consent' },
  { id: 7,  name: 'Misurazione delle prestazioni pubblicitarie',                          legalBasis: 'legitimateInterest' },
  { id: 8,  name: 'Misurazione delle prestazioni dei contenuti',                          legalBasis: 'legitimateInterest' },
  { id: 9,  name: 'Comprensione del pubblico tramite statistiche o combinazioni di dati', legalBasis: 'legitimateInterest' },
  { id: 10, name: 'Sviluppo e miglioramento dei servizi',                                 legalBasis: 'legitimateInterest' },
  { id: 11, name: 'Utilizzo di dati limitati per selezionare i contenuti',                legalBasis: 'consent' },
];

export const IAB_PURPOSES_EN = [
  { id: 1,  name: 'Store and/or access information on a device',    legalBasis: 'consent',            required: true },
  { id: 2,  name: 'Use limited data to select advertising',          legalBasis: 'consent' },
  { id: 3,  name: 'Create profiles for personalised advertising',    legalBasis: 'consent' },
  { id: 4,  name: 'Use profiles to select personalised advertising', legalBasis: 'consent' },
  { id: 5,  name: 'Create profiles to personalise content',          legalBasis: 'consent' },
  { id: 6,  name: 'Use profiles to select personalised content',     legalBasis: 'consent' },
  { id: 7,  name: 'Measure advertising performance',                 legalBasis: 'legitimateInterest' },
  { id: 8,  name: 'Measure content performance',                     legalBasis: 'legitimateInterest' },
  { id: 9,  name: 'Understand audiences through statistics',         legalBasis: 'legitimateInterest' },
  { id: 10, name: 'Develop and improve services',                    legalBasis: 'legitimateInterest' },
  { id: 11, name: 'Use limited data to select content',              legalBasis: 'consent' },
];

@Component({
  selector: 'app-cmp-form',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, JsEditorComponent],
  templateUrl: './cmp-form.component.html',
  styleUrls: ['../cmp.component.scss']
})
export class CmpFormComponent implements OnInit {
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() availableCoreVersions: string[] = ['1.0.0'];
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  // Form fields
  name = '';
  publisherId = '';
  cmpId = 1;
  configCoreVersion = '1.0.0'; // selected core version
  consentLanguage = 'IT';
  publisherCC = 'IT';
  vendorListVersion = 1;
  autoShow = true;
  publisher = false;
  selectedPurposeIds: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  selectedVendors: CmpVendorDto[] = [];
  privacyPolicyUrl = '';
  cookieName = 'tcf_consent';
  cookieExpireDays = 365;
  onConsentSavedScript = '';
  status = 1;
  selectedProviderIds: string[] = [];

  // i18n overrides
  showI18n = false;
  i18nJson = '';
  i18nError = '';

  saving = false;
  showAdvanced = false;
  providers: ProviderDto[] = [];
  tooltipConfig: { message: string; top: number; left: number } | null = null;
  private tooltipTimer: ReturnType<typeof setTimeout> | null = null;

  // GVL
  gvlVendors: CmpVendorDto[] = [];
  gvlLoading = false;
  gvlError = '';
  vendorSearch = '';

  readonly availableLanguages = ['IT', 'EN', 'DE', 'FR', 'ES', 'PL', 'NL', 'PT', 'RO', 'SV'];

  private editingId: number | null = null;
  private usedProviderIds: string[] = [];
  private originalDeployedProviders: CmpConfigProviderDto[] = [];

  constructor(
    private cmpApi: CmpApiService,
    private providersApi: ProvidersApiService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {}

  get formTitleKey(): string {
    return this.mode === 'edit' ? 'cmp.form.titleEdit' : 'cmp.form.titleCreate';
  }

  get availablePurposes() {
    return this.consentLanguage === 'IT' ? IAB_PURPOSES_IT : IAB_PURPOSES_EN;
  }

  get filteredGvlVendors(): CmpVendorDto[] {
    const term = this.vendorSearch.trim().toLowerCase();
    if (!term) return this.gvlVendors;
    return this.gvlVendors.filter(v =>
      v.name.toLowerCase().includes(term) || String(v.id).includes(term)
    );
  }

  openCreate(usedProviderIds: string[] = []): void {
    this.usedProviderIds = usedProviderIds;
    this.tooltipConfig = null;
    if (this.tooltipTimer) { clearTimeout(this.tooltipTimer); this.tooltipTimer = null; }
    this.mode = 'create';
    this.editingId = null;
    this.originalDeployedProviders = [];
    this.name = '';
    this.publisherId = this.authService.getActiveDcrId() ?? '';
    this.cmpId = 1;
    this.consentLanguage = 'IT';
    this.publisherCC = 'IT';
    this.vendorListVersion = 1;
    this.autoShow = true;
    this.publisher = false;
    this.selectedPurposeIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    this.selectedVendors = [];
    this.privacyPolicyUrl = '';
    this.cookieName = 'tcf_consent';
    this.cookieExpireDays = 365;
    this.onConsentSavedScript = '';
    this.status = 1;
    this.configCoreVersion = this.latestCoreVersion;
    this.selectedProviderIds = [];
    this.i18nJson = '';
    this.i18nError = '';
    this.showI18n = false;
    this.showAdvanced = false;
    this.vendorSearch = '';
    this.loadProviders();
    this.loadGvl();
  }

  openEdit(config: CmpConfigDto, usedProviderIds: string[] = []): void {
    this.usedProviderIds = usedProviderIds;
    this.tooltipConfig = null;
    if (this.tooltipTimer) { clearTimeout(this.tooltipTimer); this.tooltipTimer = null; }
    this.mode = 'edit';
    this.editingId = config.ID;
    this.originalDeployedProviders = config.Providers.filter(p => p.DeployStatus === 'deployed');
    this.name = config.Name;
    this.publisherId = config.PublisherID;
    this.cmpId = config.CmpId;
    this.consentLanguage = config.ConsentLanguage;
    this.publisherCC = config.PublisherCC;
    this.vendorListVersion = config.VendorListVersion;
    this.autoShow = !!config.AutoShow;
    this.publisher = !!config.Publisher;
    this.selectedPurposeIds = Array.isArray(config.SelectedPurposes) ? [...config.SelectedPurposes] : [1,2,3,4,5,6,7,8,9,10,11];
    this.selectedVendors = Array.isArray(config.Vendors) ? [...config.Vendors] : [];
    this.privacyPolicyUrl = config.PrivacyPolicyUrl ?? '';
    this.cookieName = config.CookieName || 'tcf_consent';
    this.cookieExpireDays = config.CookieExpireDays || 365;
    this.onConsentSavedScript = config.OnConsentSavedScript ?? '';
    this.status = config.Status;
    this.configCoreVersion = config.CoreVersion ?? this.latestCoreVersion;
    this.selectedProviderIds = config.Providers.map(p => p.ProviderID);
    this.i18nJson = config.I18n ? JSON.stringify(config.I18n, null, 2) : '';
    this.i18nError = '';
    this.showI18n = !!config.I18n;
    this.showAdvanced = false;
    this.vendorSearch = '';
    this.loadProviders();
    this.loadGvl();
  }

  /**
   * The config owner is never editable after creation: the deployed files live at
   * `{providerID}/cmp.js` under the owning entity, so moving a config would leave them orphaned.
   */
  get isEntityLocked(): boolean {
    return this.mode === 'edit';
  }

  /**
   * Entities selectable as owner. In global data scope that is every entity the session can see;
   * with a specific entity picked in the sidebar it is that one alone, so the field states the
   * owner instead of leaving it implicit. In edit mode it holds only the owner already stored.
   */
  get entityOptions(): { value: string; label: string }[] {
    if (this.isEntityLocked) {
      return this.publisherId
        ? [{ value: this.publisherId, label: this.authService.getDcrNameById(this.publisherId) ?? this.publisherId }]
        : [];
    }
    const available = this.authService.currentUserValue?.availableDCRs ?? [];
    if (this.authService.hasGlobalDataScope()) {
      return available.map(dcr => ({ value: dcr.id, label: dcr.Name || dcr.name || dcr.id }));
    }
    const activeDcrId = this.authService.getActiveDcrId();
    if (!activeDcrId) {
      return [];
    }
    return [{ value: activeDcrId, label: this.authService.getDcrNameById(activeDcrId) ?? activeDcrId }];
  }

  /**
   * Switching owner invalidates the provider picks: the list is scoped to the entity, so anything
   * already ticked may not belong to the new one.
   */
  onEntityChange(entityId: string): void {
    this.publisherId = entityId;
    this.selectedProviderIds = [];
    this.publisher = false;
    this.providers = [];
    this.loadProviders();
  }

  private async loadProviders(): Promise<void> {
    // Scoped to the owner entity chosen in the form, not to the sidebar selection: in global data
    // scope the sidebar has no entity, and the list would otherwise show every provider there is.
    if (!this.publisherId) {
      this.providers = [];
      return;
    }
    try {
      const all = await firstValueFrom(this.providersApi.getProviders({ status: 'active' }));
      this.providers = all.filter(p => !p.PublisherID || p.PublisherID === this.publisherId);
    } catch {}
  }

  private async loadGvl(): Promise<void> {
    this.gvlLoading = true;
    this.gvlError = '';
    try {
      const gvl = await firstValueFrom(this.cmpApi.getGvl());
      this.gvlVendors = gvl.vendors;
      if (this.mode === 'create') {
        this.vendorListVersion = gvl.vendorListVersion;
      }
    } catch {
      this.gvlError = 'GVL load failed';
    } finally {
      this.gvlLoading = false;
    }
  }

  isPurposeSelected(id: number): boolean {
    return this.selectedPurposeIds.includes(id);
  }

  togglePurpose(id: number): void {
    const idx = this.selectedPurposeIds.indexOf(id);
    if (idx >= 0) {
      // Prevent deselecting required purpose 1
      const purpose = this.availablePurposes.find(p => p.id === id);
      if (purpose?.required) return;
      this.selectedPurposeIds.splice(idx, 1);
    } else {
      this.selectedPurposeIds.push(id);
    }
  }

  isVendorSelected(vendor: CmpVendorDto): boolean {
    return this.selectedVendors.some(v => v.id === vendor.id);
  }

  toggleVendor(vendor: CmpVendorDto): void {
    const idx = this.selectedVendors.findIndex(v => v.id === vendor.id);
    if (idx >= 0) {
      this.selectedVendors.splice(idx, 1);
    } else {
      this.selectedVendors.push({ id: vendor.id, name: vendor.name, policyUrl: vendor.policyUrl });
    }
  }

  removeVendor(vendorId: number): void {
    this.selectedVendors = this.selectedVendors.filter(v => v.id !== vendorId);
  }

  get latestCoreVersion(): string {
    return this.availableCoreVersions[this.availableCoreVersions.length - 1] ?? '1.0.0';
  }

  get cmpVersion(): number {
    return this.parseSemver(this.configCoreVersion)[0];
  }

  get isCoreOutdated(): boolean {
    return this.configCoreVersion !== this.latestCoreVersion &&
      this.parseSemver(this.latestCoreVersion) > this.parseSemver(this.configCoreVersion);
  }

  get isMajorBump(): boolean {
    return this.parseSemver(this.latestCoreVersion)[0] > this.parseSemver(this.configCoreVersion)[0];
  }

  get currentCoreMajor(): number {
    return this.parseSemver(this.latestCoreVersion)[0];
  }

  private parseSemver(v: string): [number, number, number] {
    const p = (v || '1.0.0').split('.').map(Number);
    return [p[0] || 0, p[1] || 0, p[2] || 0];
  }

  get allFilteredSelected(): boolean {
    return this.filteredGvlVendors.length > 0 &&
      this.filteredGvlVendors.every(v => this.isVendorSelected(v));
  }

  toggleAllFilteredVendors(): void {
    if (this.allFilteredSelected) {
      const ids = new Set(this.filteredGvlVendors.map(v => v.id));
      this.selectedVendors = this.selectedVendors.filter(v => !ids.has(v.id));
    } else {
      for (const v of this.filteredGvlVendors) {
        if (!this.isVendorSelected(v)) {
          this.selectedVendors.push({ id: v.id, name: v.name, policyUrl: v.policyUrl });
        }
      }
    }
  }

  onConsentLanguageChange(): void {
    // Keep publisherCC in sync if it matched the old language
    if (this.publisherCC === this.consentLanguage || !this.availableLanguages.includes(this.publisherCC)) {
      this.publisherCC = this.consentLanguage;
    }
  }

  validateI18n(): boolean {
    if (!this.i18nJson.trim()) {
      this.i18nError = '';
      return true;
    }
    try {
      JSON.parse(this.i18nJson);
      this.i18nError = '';
      return true;
    } catch {
      this.i18nError = 'Invalid JSON';
      return false;
    }
  }

  isProviderDisabled(providerId: string): boolean {
    return this.usedProviderIds.includes(providerId);
  }

  toggleProvider(providerId: string): void {
    if (this.isProviderDisabled(providerId)) return;
    const idx = this.selectedProviderIds.indexOf(providerId);
    if (idx >= 0) {
      if (this.isDeployedProvider(providerId)) {
        const msg = this.translate.instant('cmp.form.removeDeployedProviderConfirm', { id: providerId });
        if (!confirm(msg)) return;
      }
      this.selectedProviderIds.splice(idx, 1);
    } else {
      this.selectedProviderIds.push(providerId);
    }
    if (providerId === this.publisherId) {
      this.publisher = this.selectedProviderIds.includes(this.publisherId);
    }
  }

  private isDeployedProvider(providerId: string): boolean {
    return this.originalDeployedProviders.some(p => p.ProviderID === providerId);
  }

  onPublisherItemClick(): void {
    if (this.publisher && this.isDeployedProvider(this.publisherId)) {
      const msg = this.translate.instant('cmp.form.removeDeployedProviderConfirm', { id: this.publisherId });
      if (!confirm(msg)) return;
    }
    this.publisher = !this.publisher;
    this.onPublisherToggle();
  }

  onPublisherToggle(): void {
    if (!this.publisher) {
      this.selectedProviderIds = this.selectedProviderIds.filter(id => id !== this.publisherId);
      return;
    }
    this.selectedProviderIds = [];
    const existing = this.providers.find(p => p.ID === this.publisherId);
    if (existing) {
      this.selectedProviderIds = [existing.ID];
    } else {
      this.selectedProviderIds = [this.publisherId];
      this.notificationService.notify('info',
        this.translate.instant('trackingScripts.messages.newProviderWillBeCreated')
      );
    }
  }

  isPublisherDisabled(): boolean {
    return this.usedProviderIds.includes(this.publisherId);
  }

  showProviderTooltip(event: MouseEvent, providerId: string): void {
    if (!this.isProviderDisabled(providerId)) return;
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.tooltipConfig = {
      message: 'Provider already used in another configuration',
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX
    };
    if (this.tooltipTimer) clearTimeout(this.tooltipTimer);
    this.tooltipTimer = setTimeout(() => { this.tooltipConfig = null; }, 2500);
  }

  get isFormValid(): boolean {
    return !!(this.name.trim() && this.publisherId && this.cmpId > 0);
  }

  async save(): Promise<void> {
    if (!this.isFormValid || this.saving) return;
    if (!this.validateI18n()) return;
    this.saving = true;

    let i18nValue: Record<string, string> | null = null;
    if (this.i18nJson.trim()) {
      try { i18nValue = JSON.parse(this.i18nJson); } catch {}
    }

    try {
      if (this.mode === 'create') {
        const body: CreateCmpConfigBody = {
          Name: this.name.trim(),
          PublisherID: this.publisherId,
          CmpId: this.cmpId,
          CoreVersion: this.configCoreVersion,
          ConsentLanguage: this.consentLanguage,
          PublisherCC: this.publisherCC,
          VendorListVersion: this.vendorListVersion,
          AutoShow: this.autoShow,
          Publisher: this.publisher,
          SelectedPurposes: [...this.selectedPurposeIds],
          Vendors: [...this.selectedVendors],
          PrivacyPolicyUrl: this.privacyPolicyUrl.trim() || null,
          I18n: i18nValue,
          CookieName: this.cookieName,
          CookieExpireDays: this.cookieExpireDays,
          OnConsentSavedScript: this.onConsentSavedScript.trim() || null,
          Status: this.status,
          ProviderIDs: [...this.selectedProviderIds]
        };
        await firstValueFrom(this.cmpApi.createConfig(body));
        this.notificationService.notify('success',
          await firstValueFrom(this.translate.get('cmp.notifications.created'))
        );
      } else {
        const body: UpdateCmpConfigBody = {
          ID: this.editingId!,
          Name: this.name.trim(),
          PublisherID: this.publisherId,
          CmpId: this.cmpId,
          CoreVersion: this.configCoreVersion,
          ConsentLanguage: this.consentLanguage,
          PublisherCC: this.publisherCC,
          VendorListVersion: this.vendorListVersion,
          AutoShow: this.autoShow,
          Publisher: this.publisher,
          SelectedPurposes: [...this.selectedPurposeIds],
          Vendors: [...this.selectedVendors],
          PrivacyPolicyUrl: this.privacyPolicyUrl.trim() || null,
          I18n: i18nValue,
          CookieName: this.cookieName,
          CookieExpireDays: this.cookieExpireDays,
          OnConsentSavedScript: this.onConsentSavedScript.trim() || null,
          Status: this.status,
          ProviderIDs: [...this.selectedProviderIds]
        };
        await firstValueFrom(this.cmpApi.updateConfig(body));
        this.notificationService.notify('success',
          await firstValueFrom(this.translate.get('cmp.notifications.updated'))
        );
      }
      this.saved.emit();
    } catch {
      this.notificationService.notify('error',
        await firstValueFrom(this.translate.get('cmp.errors.saveFailed'))
      );
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
